/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import styles from './ConsumptionFilters.module.css';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FloGroupedDropdown, FloDateRangePicker, FloFilterGroup, FloButton, FloButtonType } from 'components';
import { ConsumptionFilter, Location, LocationType } from 'model';
import { ListView } from 'model/state';
import _ from 'lodash';
import moment from 'moment-timezone';

type ConsumptionFiltersProps = {
  fetchUserLocations:       (userId: string, types?: LocationType[], page?: number, search?: string) => void,
  userLocations:            ListView<Location>,
  userId?:                  string | null,
  filters:                  ConsumptionFilter,
  updateConsumptionFilters: (filters: Partial<ConsumptionFilter>) => void,
  onRefresh:                (filters: ConsumptionFilter, timezone: string, userId?: string) => void,
  refreshDisabled:          boolean,
}

export const ConsumptionFilters: React.FC<ConsumptionFiltersProps> = React.memo(({
  fetchUserLocations,
  userLocations,
  userId,
  filters,
  updateConsumptionFilters,
  refreshDisabled,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [locationMap, setLocationMap] = useState<{[locationId: string]: Partial<Location>}>({});

  const onLocationGroupClear = () => {
    updateConsumptionFilters({ locations: [] });
    fetchUserLocations(userId as string);
  };

  const onRangeChange = (start?: string, end?: string) => {
    updateConsumptionFilters({
      dateRange: { from: start, to: end }
    });
  };

  const addToLocationMap = (e: any) => {
    const withTimezone = e?.filter((datum: any) => datum.timezone !== undefined && datum.value !== undefined) || [];
    const newValues = withTimezone.reduce((acc: {[locationId: string]: Partial<Location>}, datum: any) => {
      return {
        ...acc,
        [datum.value]: { timezone: datum.timezone },
      }
    }, {});

    setLocationMap({
      ...locationMap,
      ...newValues
    })
  };

  const handleRefreshClick = () => {
    const allTZ = _.chain(filters.locations)
      .filter(l => !!locationMap[l.id])
      .map(l => locationMap[l.id].timezone || 'UTC')
      .uniq()
      .value();
    const tz = allTZ.length === 1 ? allTZ[0] : moment.tz.guess();
    onRefresh(filters, tz || 'UTC', userId || undefined);
  };

  return (
    <Row className={styles.root}>
      <Col md="12" lg="6" xl="5">
        <FloGroupedDropdown
          id              = "locations"
          groupLabel      = {t('location-filter')}
          onClear         = {onLocationGroupClear}
          title           = {t('all-locations')}
          values          = {filters.locations.map(l => l.id)}
          multiple
          reinitList      = {() => fetchUserLocations(userId as string)}
          isLoading       = {userLocations.isLoading}
          loadingMessage  = {() => t('Loading...')}
          full
          disableMemoryFilter
          options = {userLocations.rows?.map(loc => ({ value: loc.id || '', label: loc.nickname || loc.address || '--blank--', timezone: loc.timezone })) || []}
          onChange = {e => {
            addToLocationMap(e);
            updateConsumptionFilters({ locations: e?.map((datum: any) => ({ id: datum.value, nickname: datum.label })) || [] });
          }}
          page                  = {userLocations.page || 1}
          hasMore               = {(userLocations.total || 0) > (userLocations.rows?.length || 0)}
          onLoadOptions         = {(search: string | undefined, page: number) => fetchUserLocations(userId as string, [], page, search)}
        />
      </Col>
      <Col md="12" lg="6" xl="5">
        <FloFilterGroup label={t('Date Range')} onClear={() => onRangeChange()}>
          <FloDateRangePicker
            startDate   = {filters.dateRange.from}
            endDate     = {filters.dateRange.to}
            onChange    = {onRangeChange}
          />
        </FloFilterGroup>
      </Col>
      <Col md={{ span: "3", offset: "9" }} lg={{ span: "3", offset: 9 }} xl={{ span: "2", offset: 0 }}>
        <FloButton
          className = {styles.refreshButton}
          text      = {t('Refresh')}
          onClick   = {handleRefreshClick}
          type      = {FloButtonType.DEFAULT}
          disabled  = {refreshDisabled}
          fullWidth
        />
      </Col>
    </Row>
  );
});

export default ConsumptionFilters;
